import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, UserPlus, Sparkles } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdUserId, setCreatedUserId] = useState(null);

  useEffect(() => {
    if (user && !createdUserId) navigate("/dashboard", { replace: true });
  }, [user, navigate, createdUserId]);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      setCreatedUserId(res.user_id);
      toast.success(`Welcome Dr. ${res.user.name}! Your User ID is ${res.user_id}`);
    } else {
      toast.error(res.error);
    }
  };

  if (createdUserId) {
    return (
      <AuthLayout title="Account created 🎉" subtitle="Save your User ID — you&apos;ll use it to log in.">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center" data-testid="register-success-card">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
            <Sparkles className="h-4 w-4" /> Your DearDr User ID
          </div>
          <div className="font-heading text-3xl font-extrabold tracking-[0.3em] text-blue-700 mt-3" data-testid="register-user-id">
            {createdUserId}
          </div>
          <p className="text-sm text-slate-600 mt-3">
            We&apos;ve also sent your User ID to <b>{form.email}</b>. You can log in with either your User ID or email.
          </p>
        </div>
        <Button
          onClick={() => navigate("/dashboard")}
          data-testid="register-continue-btn"
          className="w-full h-12 mt-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
        >
          Continue to Dashboard
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start your 7-day free trial. No credit card required."
      footer={
        <span>
          Already have an account?{" "}
          <Link to="/login" className="text-blue-700 font-semibold hover:underline" data-testid="register-link-login">
            Log in
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={handle("name")} placeholder="Aanya Sharma" className="mt-1.5 h-11" data-testid="register-input-name" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={handle("email")} placeholder="you@example.com" className="mt-1.5 h-11" data-testid="register-input-email" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor="phone">Phone (optional)</Label>
          <Input id="phone" type="tel" value={form.phone} onChange={handle("phone")} placeholder="+91 90000 00000" className="mt-1.5 h-11" data-testid="register-input-phone" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative mt-1.5">
            <Input
              id="password"
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={form.password}
              onChange={handle("password")}
              className="h-11 pr-10"
              data-testid="register-input-password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
              aria-label="Toggle password"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-1.5">Minimum 6 characters.</div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          {loading ? "Creating account…" : "Create account"}
        </Button>

        <p className="text-xs text-slate-500 text-center">
          By creating an account, you agree to DearDr&apos;s Terms and Privacy Policy.
        </p>
      </form>
    </AuthLayout>
  );
}
